/**
 * The phone's audio session, for the length of a call.
 *
 * On the web there is no such thing and this file is a set of no-ops: a browser
 * decides on its own which speaker a call comes out of, and there is no API to
 * argue with it. Inside either native shell there is very much such a thing, and
 * leaving it alone is what made the call quiet.
 *
 * The mechanism, twice under different names. On iOS, the moment `getUserMedia`
 * starts capturing, WebKit puts `AVAudioSession` into `playAndRecord`, whose
 * default output is the **earpiece** — correct for a phone held to a face and
 * wrong for everything else, including every video call. On Android, Chromium
 * puts `AudioManager` into `MODE_IN_COMMUNICATION` while a peer connection
 * captures, and *that* mode's default output is the earpiece too; on top of it
 * the sound leaves on `STREAM_VOICE_CALL` while the volume keys still move the
 * media stream, so the call is quiet and turning it up does nothing. Only
 * native code can override any of that, so there is a plugin on each side, and
 * this is the whole of the page's half of both.
 *
 * The Android half arrived a year late, and the delay is worth recording: the
 * speaker button was documented here as «a label on the web, a route in the
 * app», which was true and hid the fact that the Android app is the web path.
 * Every quiet call in the reports was in it.
 *
 * Nothing here is awaited by its callers. A bridge call that never answers is a
 * documented failure mode — see `withTimeout` in gallery.ts — and the way to be
 * immune to it is to have nothing waiting: a call whose audio route did not
 * change is a worse call, not a broken one, and it must never be a call that
 * does not start.
 */
import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';

/** The plugin's name on all three sides — `jsName` in CallAudioPlugin.swift, `name` in CallAudioPlugin.kt. */
export const CALL_AUDIO_PLUGIN = 'CallAudio';

interface CallAudioPlugin {
  /** Take the session for a call. `video` decides the opening route. */
  start: (options: { video: boolean }) => Promise<void>;
  /** Loudspeaker, or the earpiece. */
  setSpeaker: (options: { on: boolean }) => Promise<void>;
  /** Give it back, so music that was playing before resumes. */
  stop: () => Promise<void>;
  /**
   * Playback category while ringing, so the ringtone survives the mute switch.
   * A deliberate no-op on Android, which has neither — see the Kotlin plugin.
   */
  ringing: (options: { on: boolean }) => Promise<void>;
  addListener: {
    (
      event: 'callAudioInterruption',
      handler: (info: { began: boolean }) => void,
    ): Promise<PluginListenerHandle>;
    (
      event: 'callAudioRoute',
      handler: (info: { speaker: boolean; port: string; detail?: string }) => void,
    ): Promise<PluginListenerHandle>;
  };
}

const CallAudio = registerPlugin<CallAudioPlugin>(CALL_AUDIO_PLUGIN);

interface CapacitorBridge {
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
}

/**
 * True inside either app, and only where the plugin is really registered.
 *
 * Both halves matter, for the reason `hasNativeVideoNote` gives: a web bundle
 * newer than the native half it is running inside must take the web path rather
 * than call into nothing. Here that is the difference between a speaker button
 * that does nothing and a call that never starts — and it is the whole of what
 * happens to an Android phone that has not taken the build carrying the Kotlin
 * plugin yet, which is why the platform check is not enough on its own.
 */
export const hasNativeCallAudio: boolean = (() => {
  const capacitor = (globalThis as { Capacitor?: CapacitorBridge }).Capacitor;
  const platform = capacitor?.getPlatform?.();
  if (platform !== 'ios' && platform !== 'android') return false;
  return capacitor?.isPluginAvailable?.(CALL_AUDIO_PLUGIN) === true;
})();

/** Every call below is this: fire it, and never wait for it. */
function tell(work: () => Promise<unknown>): void {
  if (!hasNativeCallAudio) return;
  void work().catch(() => {
    // A route that could not be set is a call that sounds wrong, and there is
    // nothing useful to do about it from here. The plugin logs its own side.
  });
}

/**
 * Claim the session for a call.
 *
 * Call this **after** the local stream has been acquired, not before. Both
 * engines configure the session themselves when capture starts, so anything set
 * first is silently replaced a moment later — which looks exactly like a plugin
 * that does not work. Both plugins re-apply on route changes for the same
 * reason.
 */
export function startCallAudio(video: boolean): void {
  tell(() => CallAudio.start({ video }));
}

export function setCallSpeaker(on: boolean): void {
  tell(() => CallAudio.setSpeaker({ on }));
}

export function stopCallAudio(): void {
  tell(() => CallAudio.stop());
}

/** Playback category while a call is ringing here, and back off afterwards. */
export function setCallRinging(on: boolean): void {
  tell(() => CallAudio.ringing({ on }));
}

/**
 * What the session says while a call is on: a real phone call interrupting, and
 * headphones going in or out.
 *
 * Returns the unsubscribe, and returns it synchronously — `addListener` is a
 * promise, and an effect that awaited it would be an effect whose cleanup can
 * run before its subscription exists.
 */
export function watchCallAudio(handlers: {
  onInterruption: (began: boolean) => void;
  onRoute: (speaker: boolean, where: string) => void;
}): () => void {
  if (!hasNativeCallAudio) return () => {};
  const pending: Promise<PluginListenerHandle>[] = [
    CallAudio.addListener('callAudioInterruption', (info) => handlers.onInterruption(info.began)),
    // The route is two facts, not one. The button wants the boolean; the call's
    // journal wants the sentence, because «out of which end, at what volume
    // setting» is the question a quiet call asks and the one nothing else here
    // can answer. The Android plugin fills `detail` in; the iOS one does not yet.
    CallAudio.addListener('callAudioRoute', (info) =>
      handlers.onRoute(info.speaker, info.detail ? `${info.port} · ${info.detail}` : info.port),
    ),
  ];
  return () => {
    for (const handle of pending) void handle.then((entry) => entry.remove()).catch(() => {});
  };
}
